import { create } from 'zustand'
import { Appearance } from 'react-native'
import { ThemePrefs } from '../storage/theme-storage'
import { resolveTheme } from '../theme/resolve-theme'
import type { ThemeMode, ResolvedTheme } from '../theme/theme.types'

interface ThemeStoreState {
  /** What the user has explicitly chosen inside Tago. Defaults to light. */
  mode: ThemeMode

  /**
   * The actual theme being applied right now.
   * Computed from mode + current system theme.
   */
  resolvedTheme: ResolvedTheme

  /**
   * Current OS color scheme (injected by ThemeBootstrap at runtime).
   * Not persisted — always read fresh from the OS, but never used as the
   * default app theme.
   */
  systemTheme: ResolvedTheme

  /** Change the user's preference. Persists to MMKV immediately. */
  setMode: (mode: ThemeMode) => void

  /**
   * Called by ThemeBootstrap whenever the OS scheme changes.
   * Re-evaluates resolvedTheme from the new system value.
   */
  setSystemTheme: (systemTheme: ResolvedTheme) => void

  /** Restore to factory defaults (light). */
  reset: () => void
}

const initialMode = ThemePrefs.getMode()
// Read OS color scheme SYNCHRONOUSLY at module init — no flicker on boot
const initialSystemTheme: ResolvedTheme =
  (Appearance.getColorScheme() ?? 'light') === 'dark' ? 'dark' : 'light'

export const useThemeStore = create<ThemeStoreState>((set, get) => ({
  mode: initialMode,
  resolvedTheme: resolveTheme(initialMode, initialSystemTheme),
  systemTheme: initialSystemTheme,

  setMode: (mode) => {
    ThemePrefs.setMode(mode)
    const resolved = resolveTheme(mode, get().systemTheme)
    set({ mode, resolvedTheme: resolved })
  },

  setSystemTheme: (systemTheme) => {
    const resolved = resolveTheme(get().mode, systemTheme)
    set({ systemTheme, resolvedTheme: resolved })
  },

  reset: () => {
    ThemePrefs.setMode('light')
    const { systemTheme } = get()
    set({
      mode: 'light',
      resolvedTheme: resolveTheme('light', systemTheme),
    })
  },
}))
